import {
  CART_RESOURCE_ROOT,
  OPEN_CART_RESOURCE_ROOT,
  getResourceRoot,
} from "Utils/apiUtils";
import { call, put, select, takeLatest } from "redux-saga/effects";
import {
  DiscountResponse,
  NewCartResponse,
  NewCustomerResponse,
  OperationType,
  UserIdentificationData,
} from "./userIdentificationInterfaces";
import {
  operationFailed,
  userIdentificationCheckExistingCartStart,
  userIdentificationExistingCartStart,
  userIdentificationStart,
  userIdentificationSucceeded,
  userIdentificationV2Start,
  userIdentificationV2Succeeded,
} from "./userIdentificationSlice";
import {
  createGetDiscountRequest,
  createNewCartRequest,
  createNewCustomerRequest,
  createUpdateCustomerInfoRequest,
  isPartnerMismatch,
} from "./userIdentificationUtils";

import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { getAgreementProduct } from "Components/Features/CheckPrice/helpers/getAgreementProduct";
import { partnerSelector } from "Store/addProductToCart/sagas";
import { isAdvisorSelector } from "Store/advisor/advisorSelectors";
import { Cart } from "Store/cart/cartInterfaces";
import { fetchCartSucceeded } from "Store/cart/cartSlice";
import { isToggled } from "Store/featureToggles/featureToggleSaga";
import { FeatureToggle } from "Store/featureToggles/featureToggleTypes";
import { PartnerState } from "Store/partners/partnerInterfaces";
import {
  getIdTokenParsed,
  isAuthenticated,
  loginIfNeeded,
} from "Utils/KeycloakWrapper";
import { isSentOrAcceptedOfferFromAdvisor } from "Utils/cartUtils";
import { redirectToLoginPage } from "Utils/navigation/redirectUtil";
import { request } from "Utils/requestUtils";
import { Log } from "Utils/sentryLogUtils";
import { getNewCustomerNin } from "./getNewCustomerNin";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* userIdentificationCheckExistingCart({
  payload,
}: ReturnType<typeof userIdentificationCheckExistingCartStart>) {
  const partner: PartnerState = yield select(partnerSelector);
  const cartId = payload.cartId;

  if (cartId) {
    const cart: Cart = yield* request("get", operationFailed, {
      type: OperationType.GET_CART_REQUEST,
      payload: { url: `${OPEN_CART_RESOURCE_ROOT}/${payload.cartId}` },
    });

    if (isPartnerMismatch(cart.agreementProduct, partner.partnerId)) {
      yield call(loginIfNeeded);
      yield put(userIdentificationStart(payload));
    } else {
      yield put(
        userIdentificationExistingCartStart({
          cartId: payload.cartId,
          cartIdFromCookie: payload.cartIdFromCookie,
        }),
      );
    }
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* userIdentification({
  payload,
}: ReturnType<typeof userIdentificationStart>) {
  try {
    const partner: PartnerState = yield select(partnerSelector);

    let loginInfo = {
      no_nin: "",
      given_name: "",
      family_name: "",
    };

    if (!payload.ssn) {
      loginInfo = yield call(getIdTokenParsed);
    }

    const payloadLoginInfo = {
      ssn: payload.ssn ?? loginInfo.no_nin,
      firstName: payload.firstName ?? loginInfo.given_name,
      lastName: payload.lastName ?? loginInfo.family_name,
      products: payload.products,
    };

    const discountResponse = yield* request("post", operationFailed, {
      type: OperationType.GET_DISCOUNT_REQUEST,
      payload: createGetDiscountRequest(payloadLoginInfo, partner),
    });
    const newCartResponse = yield* request("post", operationFailed, {
      type: OperationType.CREATE_NEW_CART_REQUEST,
      payload: createNewCartRequest(payloadLoginInfo),
    });
    const customerInfoResponse = yield* request("post", operationFailed, {
      type: OperationType.UPDATE_CUSTOMER_INFO_REQUEST,
      payload: createUpdateCustomerInfoRequest(
        discountResponse,
        newCartResponse,
      ),
    });
    let newCustomerResponse = undefined;
    if (isAuthenticated()) {
      newCustomerResponse = yield* request("get", operationFailed, {
        type: OperationType.GET_CUSTOMER_REQUEST,
        payload: { url: newCartResponse?._links.customer[0].href },
      });
      if (payload.products === ProductBackendName.ADVICE) {
        const customerV2 = yield* request("get", operationFailed, {
          type: OperationType.GET_CUSTOMER_REQUEST,
          payload: {
            url: `${CART_RESOURCE_ROOT}/customers/${newCustomerResponse.customerRef}`,
            open: false,
          },
        });
        newCustomerResponse.providedContactInfo.address = customerV2.address;
      }
    } else {
      newCustomerResponse = yield* request("patch", operationFailed, {
        type: OperationType.CREATE_NEW_CUSTOMER_REQUEST,
        payload: createNewCustomerRequest(payloadLoginInfo, newCartResponse),
      });
    }

    const user = {
      nin: payload.ssn,
      discountResponse,
      newCartResponse,
      customerInfoResponse,
      newCustomerResponse,
    } as UserIdentificationData;

    yield put(userIdentificationSucceeded(user));
  } catch (e) {
    if (typeof e === "string") {
      Log.error(
        `Operation failed for type ${OperationType.USER_IDENTIFICATION} - ${e}`,
        {
          extras: {
            failedOperationType: e,
          },
        },
      );
    } else {
      Log.error(
        `Operation failed for type ${OperationType.USER_IDENTIFICATION} - Unknown`,
      );
    }
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* userIdentificationV2({
  payload,
}: ReturnType<typeof userIdentificationV2Start>) {
  try {
    if (isAuthenticated()) {
      const customerV2 = yield* request("get", operationFailed, {
        type: OperationType.GET_CUSTOMER_REQUEST,
        payload: {
          url: `${CART_RESOURCE_ROOT}/customers/${payload.customerRef}`,
          open: false,
        },
      });

      yield put(userIdentificationV2Succeeded(customerV2.address));
    } else {
      throw new Error("User is not authenticated");
    }
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.USER_IDENTIFICATION_V2,
      }),
    );
  }
}

export function* userIdentificationExistingCart({
  payload,
}: ReturnType<typeof userIdentificationExistingCartStart>): any {
  try {
    // TODO need to replace NewCartResponse with v2 getCart
    const isAdvisor: boolean = yield select(isAdvisorSelector);
    let cartUrl = `/open/insurance/carts/${payload.cartId}`;
    if (isAdvisor) {
      cartUrl = cartUrl.replace("/open", "");
    }
    const redirectToggle = yield isToggled(
      FeatureToggle.REDIRECT_TO_LOGIN_PAGE,
    );

    if (redirectToggle && !isAdvisor) {
      redirectToLoginPage();
    }

    const getCartResponse: NewCartResponse = yield* request(
      "get",
      operationFailed,
      {
        type: OperationType.GET_CART_REQUEST,
        payload: { url: cartUrl, open: !isAdvisor },
      },
    );

    const getCustomerResponse: NewCustomerResponse = yield* request(
      "get",
      operationFailed,
      {
        type: OperationType.GET_CUSTOMER_REQUEST,
        payload: {
          url: getCartResponse?._links.customer[0].href,
          open: !isAdvisor,
        },
      },
    );

    const f2100PropertiesProcessor =
      "no.storebrand.orders.f2100.processors.F2100PropertiesProcessor";
    const {
      agreementProductFromDiscount,
      isNewInsuranceCustomerFromDiscount: isNewInsuranceCustomer,
      paymentPeriod,
    } = getCustomerResponse.properties[f2100PropertiesProcessor];

    const agreementProduct = getAgreementProduct({
      manualAgreementProduct: getCustomerResponse.manualAgreementProduct,
      agreementProductFromDiscount,
    });

    const discountResponse: DiscountResponse = {
      f2100: { agreementProduct, isNewInsuranceCustomer, paymentPeriod },
    };

    const getCartV2Response: Cart = yield* request("get", operationFailed, {
      type: OperationType.GET_CART_REQUEST,
      payload: {
        url: `${getResourceRoot()}/${payload.cartId}`,
        open: !isAuthenticated(),
      },
    });

    if (
      payload.cartIdFromCookie &&
      isSentOrAcceptedOfferFromAdvisor(getCartV2Response)
    ) {
      yield put(
        operationFailed({
          operationType: OperationType.GET_OFFER_FROM_ADVISOR_REQUEST,
        }),
      );
    } else {
      if (getCartV2Response.advice) {
        yield put(fetchCartSucceeded(getCartV2Response));
      }
      yield put(
        userIdentificationSucceeded({
          discountResponse,
          newCartResponse: getCartResponse,
          customerInfoResponse: {},
          newCustomerResponse: getCustomerResponse,
          nin: isAdvisor ? getNewCustomerNin(getCustomerResponse) : undefined,
        } as UserIdentificationData),
      );
    }
  } catch (e) {
    Log.error(
      `Operation failed for type ${OperationType.USER_IDENTIFICATION_EXISTING_CART}`,
    );
  }
}

export function* userIdentificationSaga(): Generator {
  yield takeLatest(userIdentificationStart, userIdentification);
  yield takeLatest(userIdentificationV2Start, userIdentificationV2);
  yield takeLatest(
    userIdentificationExistingCartStart,
    userIdentificationExistingCart,
  );
  yield takeLatest(
    userIdentificationCheckExistingCartStart,
    userIdentificationCheckExistingCart,
  );
}
